import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import apiService from '../../../services/api';
import { toast } from 'react-toastify';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword?: string;
}

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordForm>();

  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  // Watch newPassword to compare with confirmPassword
  const newPasswordValue = watch('newPassword');

  const onSubmit = (data: ChangePasswordForm) => {
    const finalData = {
      currentPassword: data?.currentPassword ? data?.currentPassword : '',
      newPassword: data?.newPassword ? data?.newPassword : '',
    };

    try {
      apiService.changePassword(finalData);
      toast.success('Password changed successfully');
    } catch (err: any) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      toast.error(apiError);
    }
    reset();
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Change Password
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <form id="changePasswordForm" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* CURRENT PASSWORD */}
            <Grid item xs={12}>
              <TextField
                label="Current Password"
                type={showCurrent ? 'text' : 'password'}
                fullWidth
                {...register('currentPassword', {
                  required: 'Current password is required',
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowCurrent(prev => !prev)}>
                        {showCurrent ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword?.message}
              />
            </Grid>
            {/* NEW PASSWORD */}
            <Grid item xs={12}>
              <TextField
                label="New Password"
                type={showNew ? 'text' : 'password'}
                fullWidth
                {...register('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew(prev => !prev)}>
                        {showNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* CONFIRM PASSWORD */}
            <Grid item xs={12}>
              <TextField
                label="Confirm New Password"
                type={showConfirm ? 'text' : 'password'}
                fullWidth
                {...register('confirmPassword', {
                  required: 'Confirm password is required',
                  validate: value =>
                    value === newPasswordValue || 'Passwords do not match',
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm(prev => !prev)}>
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ m: '10px' }}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" form="changePasswordForm">
          Update Password
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
